import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import {
  Order,
  OrdersService,
  WatchAlert,
} from '@compumark/order-management-client';
import { cloneDeep } from 'lodash';
import { Observable, shareReplay } from 'rxjs';

import { SnackbarService } from '../../services/snackbar/snackbar.service';
import {
  AdjustHitOverviewComponent,
  UiHitOverview,
} from '../adjust-hit-overview/adjust-hit-overview.component';
import { columnFields } from '../adjust-hit-overview/all-watch-results-columns';
import { AlertGridOptionsService } from '../alert-columns/alert-column-definition/alert-grid-options.service';
import { AlertSettingsComponent } from '../alert-settings/alert-settings.component';
import { AlertApiClientService } from '../services/alert-api-client.service';
import { toWatchAlertRequestDTO } from '../services/watch-alert-request-utils';

@Component({
  selector: 'app-create-new-alert',
  templateUrl: './create-new-alert.component.html',
  styleUrls: ['./create-new-alert.component.scss'],
  standalone: false,
})
export class CreateNewAlertComponent implements OnInit {
  private data = inject<WatchAlert>(MAT_DIALOG_DATA);
  private snackBar = inject(SnackbarService);
  private orderService = inject(OrdersService);
  private alertClientService = inject(AlertApiClientService);
  private alertGridOptionsService = inject(AlertGridOptionsService);
  dialogRef = inject<MatDialogRef<CreateNewAlertComponent>>(MatDialogRef);

  public readonly STEP_LABELS = [
    'Add Watch Orders',
    'Alert Settings',
    'Adjust Hit Overview',
  ];
  public readonly ACTION_BUTTON_LABELS = [...this.STEP_LABELS, 'Create Alert'];
  public orders$: Observable<Order[]>;
  columns: UiHitOverview[] = [];

  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('alertSettings') alertSettings!: AlertSettingsComponent;
  @ViewChild('adjustHitOverview')
  adjustHitOverview!: AdjustHitOverviewComponent;

  constructor() {
    this.orders$ = new Observable<Order[]>();
  }

  ngOnInit(): void {
    this.orders$ = this.loadActiveOrders();
    this.columns = cloneDeep(columnFields);
  }

  private loadActiveOrders(): Observable<Order[]> {
    return this.orderService.getOrders('active').pipe(shareReplay(1));
  }

  get currentStepIndex(): number {
    return this.stepper?.selectedIndex ?? 0;
  }

  goToNextStep(): void {
    const currentIndex = this.currentStepIndex;
    const steps = this.stepper?.steps?.toArray() ?? [];
    if (steps[currentIndex]) {
      steps[currentIndex].completed = true;
    }

    if (currentIndex < steps.length - 1) {
      this.stepper?.next();
    }
  }

  goToPrevStep(): void {
    const currentIndex = this.currentStepIndex;
    const steps = this.stepper?.steps?.toArray() ?? [];
    if (steps[currentIndex]) {
      steps[currentIndex].completed = false;
    }
    this.stepper?.previous();
  }

  isDisabled(): boolean {
    if (!this.stepper) {
      return true;
    }

    const stepIndex = this.currentStepIndex;

    switch (stepIndex) {
      case 0:
        return !this.data?.orders?.length;
      case 1: {
        const title = this.data?.title?.trim();
        const subject = this.data?.subject?.trim();
        const hasDuplicate =
          this.alertSettings?.alertSettingsForm
            ?.get('alertTitle')
            ?.hasError('duplicateTitle') ?? false;

        return !title || !subject || hasDuplicate;
      }
      case 2:
        if (this.adjustHitOverview?.data?.hitOverView?.enabled) {
          return !this.columns?.some((col) => col.checked);
        }
        return false;
      default:
        return true;
    }
  }

  onCreateAlert(): void {
    if (this.adjustHitOverview?.data?.hitOverView?.enabled) {
      this.adjustHitOverview.toHitOverviewField(this.columns);
    }

    this.alertClientService
      .createAlert(toWatchAlertRequestDTO(this.data))
      .subscribe({
        next: (createdAlert) => {
          this.dialogRef.close();
          this.snackBar.openSnackBar(
            `Your alert "${createdAlert.title}" has been created`,
          );
          this.alertGridOptionsService.addHighlightedRow(createdAlert);
        },
      });
  }
}
